import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Home, Support, Privacy, Terms, Profile, ChangePassword, Tasks, AssignedTasks,
         Task, Items, Item, ItemImage, EmailItem,
         Logbook, LogbookEntry, LogbookPickTask, LogbookEntryFromTask, LogbookRedirect, LogbookTrip,
         LogbookYear,
         AllTrips, Trips, TripsCalendarRedirect, Trip, TripGroup, Places, Place, PlaceVisits,
         Test, SharedTasksView, Unsubscribe, Salish,
         SharedTrip, SharedTripItinerary, SharedTripLeg,
         SharedLogbookTrip, SharedLogbookTripEntries, SharedLogbookEntry,
         SharedAssignedTasks } from "./pages";
import { Navigation, Footer, PageNotFound, SaveStatusPill } from "./components";
import { Authenticator } from '@aws-amplify/ui-react';
import { Hub } from '@aws-amplify/core';
import { signOut } from 'aws-amplify/auth';
import { authServices } from './auth/authServices';
import { createAuthStateHandler } from './auth/authState';
import WarmedOutlet from './auth/WarmedOutlet';
import { ErrorBoundary } from "react-error-boundary";
import { useStatusMessage } from './components/StatusMessage';
import { AmplifyThemeBridge } from './theme/AmplifyThemeBridge';
import { usePendingSavesUnloadWarning } from './utilities/useUnsavedChangesGuard';
import * as Urls from './fleet-shared/Urls.mjs';

import '@aws-amplify/ui-react/styles.css';

import './App.css';

// For debugging purposes, spoofs the database/auth.
export const testMode = false;

// Shown when there's an uncaught exception
const CaughtError = () => (
  <div className="container text-center py-5">
    <div
      className="d-inline-flex align-items-center justify-content-center mb-3 rounded-circle"
      style={{
        width: 72,
        height: 72,
        background: 'var(--tp-surface-2)',
        color: 'var(--tp-danger)',
      }}
      aria-hidden="true"
    >
      <i className="fa-solid fa-triangle-exclamation fa-2x"></i>
    </div>
    <h2 className="text-danger">Something went wrong on our end.</h2>
    <p className="text-body-secondary">
      If the problem persists, please <a href={Urls.urlForSupport()}>contact us</a>.
    </p>
    <p><a href="/">Home</a></p>
  </div>
);

// Wraps children in an authenticator
const RequireAuth = () => {
  // In test mode, no auth -- just pretend user is logged in.
  if (testMode) {
    return <Outlet/>
  }

  // Otherwise use the Authenticator, themed via the Amplify bridge.
  // WarmedOutlet holds the pages back until the auth session has been warmed
  // once, so their mount-time GraphQL calls hit a warm credentials cache
  // instead of stampeding Cognito Identity (see src/auth/sessionWarm.js).
  return (
    <AmplifyThemeBridge>
      <Authenticator services={authServices}>
        {() => <WarmedOutlet />}
      </Authenticator>
    </AmplifyThemeBridge>
  );
}


function App() {
  // I want to know the signed in user. This is async in React, so I get it at app startup and update if changes
  // See: https://github.com/aws-amplify/amplify-js/issues/3640
  // I add a few additional attributes to the user from Auth -- email, email_verified, and identityId.
  let [user, setUser] = useState(null);
  let [isInitializing, setIsInitializing] = useState(true);
  let [StatusMessage, {/*showLoading, showText, */ showError, hideStatus}] = useStatusMessage();

  // Warn on tab close / refresh while any autosave is still pending or in
  // flight. Mounted here, once, so it covers every edit surface -- the modal
  // editors don't mount useUnsavedChangesGuard, and with no Save button the
  // user has no other signal that an edit hasn't landed yet.
  usePendingSavesUnloadWarning();

  // For auth:
  useEffect(() => {
    let updateUser = createAuthStateHandler({
      setUser,
      setIsInitializing,
      hideStatus,
      showError,
      redirectHome: () => window.location.assign('/'),
      getPathname: () => window.location.pathname,
    });

    Hub.listen('auth', updateUser) // listen for login/signup events
    updateUser() // check manually the first time because we won't get a Hub event
    return () => Hub.remove('auth', updateUser) // cleanup
  }, [hideStatus, showError]);



  async function signOutUser() {
    try {
      await signOut();
    } catch (error) {
      // Show user the error...
      console.error('error signing out: ', error);
      showError(error);
    }
  }


  // Home - Describes product
  // support a product support page
  // me/item list of items the user is tracking (like a list of boats)
  // me/item/:id - Lists one item (a top level item, like a vessel or home)
  // me/item/:id/tasks(/:type) - A list of tasks belonging to the item with the given ID. Type is optional, omitted = all.
  // me/profile - Logged in users profile.
  // Page not found for everything else.
  return (
    <ErrorBoundary fallback={<CaughtError/>}>
      <main>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Navigation user={user} isInitializing={isInitializing} signOut={signOutUser} />
          <StatusMessage />
          <SaveStatusPill />

          <Routes>
            <Route path='/' element={<Home user={user} isInitializing={isInitializing}/>}/>
            <Route path='salish' element={<Salish/>}/>
            <Route path='support' element={<Support/>}/>
            <Route path='privacy' element={<Privacy/>}/>
            <Route path='terms' element={<Terms/>}/>
            <Route path='unsubscribe' element={<Unsubscribe/>}/>
            {/* Public shared-trip view: anyone holding the (unguessable,
                revocable) link can open it with no account, so it lives out
                here with the other public routes, not under /me. */}
            <Route path='shared/trip/:shareId' element={<SharedTrip/>}>
              <Route index element={<SharedTripItinerary/>}/>
              <Route path='leg/:legId' element={<SharedTripLeg/>}/>
            </Route>
            {/* The same, for a Captain's Log trip: the entries, their notes
                and their photos. Photos are presigned by the Lambda, since
                this route has no Amplify Storage. */}
            <Route path='shared/logbook/:shareId' element={<SharedLogbookTrip/>}>
              <Route index element={<SharedLogbookTripEntries/>}/>
              <Route path='entry/:entryId' element={<SharedLogbookEntry/>}/>
            </Route>
            {/* And for one item's tasks assigned to one person: the live list,
                read-only, off the share-assigned-tasks Function URL. */}
            <Route path='shared/tasks/:shareId' element={<SharedAssignedTasks/>}/>
            <Route path='404.html' element={<PageNotFound/>}/>
            <Route path='test' element={<Test/>} />
            <Route path='me/*' element={<RequireAuth/>}>
              <Route path='shared/:shareId' element={<SharedTasksView/>}/>
              <Route path='profile' element={<Profile user={user} signOut={signOutUser}/>}/>
              <Route path='changepass' element={<ChangePassword user={user}/>}/>
              {/* Cross-item Trips (top-nav). Trips are still item-scoped
                  underneath -- this page just groups them by item and links
                  into the same /me/item/:itemId/... pages. */}
              <Route path='trips' element={<AllTrips/>}/>
              {/* The shared gazetteer (TaskPogoPlace) behind every waypoint
                  name. Not item-scoped: one table, shared by every user. */}
              <Route path='places' element={<Places/>}/>
              <Route path='places/:placeId' element={<Place/>}/>
              {/* Everything logged at one place -- the other direction from the
                  rest of the app, where a place row is invisible machinery
                  behind a waypoint name. */}
              <Route path='places/:placeId/visits' element={<PlaceVisits/>}/>
              <Route path='item/*' element={
                <Routes>
                  <Route path='/' element={<Items user={user}/>}/>
                  <Route path=':itemId/*' element={
                    <Routes>
                      <Route path='/' element={<Item user={user}/>}/>
                      <Route path='email' element={<EmailItem user={user}/>}/>
                      <Route path='task/:taskId' element={<Task user={user}/>}/>
                      <Route path='tasks' element={<Tasks user={user}/>}/>
                      {/* One person's tasks (?to=<name>). The static segment
                          outranks the dynamic :listType beside it whatever the
                          order; it sits above to say which is meant. */}
                      <Route path='tasks/assigned' element={<AssignedTasks user={user}/>}/>
                      <Route path='tasks/:listType' element={<Tasks user={user}/>}/>
                      <Route path='trips' element={<Trips user={user}/>}/>
                      {/* Legacy path (bookmarks, links sent to the crew): the
                          calendar now renders below the trips list. */}
                      <Route path='trips/calendar' element={<TripsCalendarRedirect/>}/>
                      <Route path='trips/group/:groupKey' element={<TripGroup user={user}/>}/>
                      <Route path='trip/:tripId' element={<Trip user={user}/>}/>
                      <Route path='logbook/*' element={
                        <Routes>
                          {/* Legacy path (status emails, bookmarks): redirect
                              to the item's first logbook or back to the item. */}
                          <Route path='/' element={<LogbookRedirect user={user}/>}/>
                          <Route path=':logbookId/*' element={
                            <Routes>
                              <Route path='/' element={<Logbook user={user}/>}/>
                              <Route path='picktask' element={<LogbookPickTask user={user}/>}/>
                              <Route path='fromtask/:taskId' element={<LogbookEntryFromTask user={user}/>}/>
                              {/* One trip's entries, keyed by the trip's id
                                  (there is still no trip row -- see
                                  TripIdentity.mjs). Above :entryId -- two
                                  segments outrank one dynamic one either way,
                                  but the order says which is meant. */}
                              <Route path='trip/:tripKey' element={<LogbookTrip user={user}/>}/>
                              {/* One year's trips, reached from the summary
                                  card's year rows. Same reason it sits here. */}
                              <Route path='year/:year' element={<LogbookYear user={user}/>}/>
                              <Route path=':entryId' element={<LogbookEntry user={user}/>}/>
                              <Route path="*" element={<PageNotFound/>}/>
                            </Routes>
                          }/>
                          <Route path="*" element={<PageNotFound/>}/>
                        </Routes>
                      }/>
                      <Route path='image' element={ <ItemImage user={user} /> } />
                      <Route path='*' element={<PageNotFound/>}/>
                    </Routes> 
                  }/>
                </Routes>
              }/>
              <Route path="*" element={<PageNotFound/>}/>
            </Route>
            <Route path="*" element={<PageNotFound/>}/>
          </Routes>
          <Footer />
        </BrowserRouter>
      </main>
    </ErrorBoundary>
  );
}

export default App;
